// Who holds a city, as the map shows it. Slate & Fire map (Oct 2026): the relationship colours the castle's roofs and
// cloth (ready-coloured PNGs) and its pennant; the old territory washes under the keeps are gone.
// Relations come from village/get: your own cities (player_details[uid].target_char.villages), your guild
// (guild_info.id) and its diplomacy (guild_info.diplomacy.a / n / e — objects of { tag: gid }), exactly the way the
// phone colours its castles (Map/MapChunkScript.cs GenerateMixTexture).
import type { Village } from "@/api/types";
import type { Cell } from "./chunks";
import { ownVillages } from "@/state/queries";

export type Relation = "you" | "guild" | "ally" | "nap" | "enemy" | "unaligned" | "barbarian";

export interface Relations {
  /** "x,y" of your own cities */
  own: Set<string>;
  gid: number;
  allies: Set<number>;
  naps: Set<number>;
  enemies: Set<number>;
}

export function relationsOf(v: Village | undefined, uid: number | null): Relations {
  const own = new Set<string>();
  for (const c of ownVillages(v, uid)) own.add(c.coords);
  const g = v?.guild_info as { id?: number; diplomacy?: { a?: Record<string, number>; n?: Record<string, number>; e?: Record<string, number> } } | undefined;
  const ids = (o?: Record<string, number>) => new Set(Object.values(o ?? {}).map(Number).filter((n) => n > 0));
  return { own, gid: g?.id ?? 0, allies: ids(g?.diplomacy?.a), naps: ids(g?.diplomacy?.n), enemies: ids(g?.diplomacy?.e) };
}

export function relationOf(cell: Cell, r: Relations): Relation {
  if (cell.barbarian) return "barbarian";
  if (r.own.has(`${cell.x},${cell.y}`)) return "you";
  if (cell.guildId > 0 && r.gid > 0) {
    if (cell.guildId === r.gid) return "guild";
    if (r.allies.has(cell.guildId)) return "ally";
    if (r.enemies.has(cell.guildId)) return "enemy";
    if (r.naps.has(cell.guildId)) return "nap";
  }
  return "unaligned";
}

/** The approved relationship palette (Slate & Fire map, Oct 11 2026): roofs, cloth and pennant of the painted castles, the
 *  distant markers, and the sheet and legend keys. A barbarian (NPC) city has no relationship colour: its Log Stockade in
 *  the artwork's own colours (Oct 12 2026), no pennant. */
export const REL_COLORS: Record<Relation, { body: string | null; label: string }> = {
  you: { body: "#F7D54A", label: "You" },
  guild: { body: "#3E8EF5", label: "Your guild" },
  ally: { body: "#28C7BC", label: "Ally" },
  nap: { body: "#AB70ED", label: "Non-aggression" },
  enemy: { body: "#E34843", label: "Enemy" },
  unaligned: { body: "#C0B6A0", label: "Unaligned" },
  barbarian: { body: null, label: "Barbarian" },
};

/** A barbarian city's marker when the map is too far out for its stockade: faded stone. */
export const BARBARIAN_MARK = "#5E5A52";

/** Dot colours for the realm view: relationships first, then a palette for the other guilds by size.
 *  Oct 2026: with the Slate & Fire relationship palette, the old slots that sat on top of a relationship colour —
 *  #C94FD8 and #B08CFF (≈ non-aggression), #D9C84A and #F0D08C (≈ you), #4FC8D8 (≈ ally) — were replaced; orange, green
 *  and pink keep their slots. Every colour here is at least ΔE2000 16.8 from every relationship colour, the barbarian
 *  marker, the selection's ivory edge (#FFFFED) and each other (checked in tests/unit/slateFire.test.ts). */
export const REALM_PALETTE = ["#DCC9ED", "#7CC9FF", "#F08A3C", "#8E2F5E", "#8CD84F", "#E86FA0", "#A66A3A", "#0F408A"];
export const REALM_GREY = "#8E8E8E";
